import serverSideTracking from 'components/shared/productItem/shared/helpers';
import RequestContext from 'modules/requestContext';
import { addProductAndSubscriptionToReceipt, addToReceipt } from 'pos/shared/fiftytwo/fiftyTwoApi';
import { type EventParamDecorated, addToCartTypeEventCategoryMapper } from 'modules/tracking/GA4';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';
import createActionTracking from '../../shared/helpers';
import type { IProductActionTrackingContext, ProductActionProps } from '../../shared/types';
import usePointOfSaleRequest from 'hooks/user/usePointOfSaleRequest';

function getEventLabelByContext({
    eventCategory,
    eventAction,
    eventLabel,
    type,
    params
}: IProductActionTrackingContext<{ recommendationPlacement?: number | string }>) {
    let contextEventLabel;

    switch (type) {
        case 'Bundle':
            contextEventLabel = 'bundleButton';
            break;
        case 'Epaper':
            contextEventLabel = 'epaperButton';
            break;
        case 'ProductGroupRecomendation':
            contextEventLabel = params?.recommendationPlacement;
            break;
        default:
            contextEventLabel = eventLabel;
    }

    return { eventAction, eventCategory, eventLabel: contextEventLabel };
}

export const loadAddToBasketFunc = async () => {
    try {
        const { addToBasket } = await import(
            /* webpackChunkName: "addAndRemoveFromBasket" */ 'modules/addAndRemoveFromBasket'
        );

        return addToBasket;
    } catch (error) {
        throw new Error('failed to load addToBasket');
    }
};

async function addToBasketPos(params: ProductActionProps['BuyNow']['params']) {
    try {
        if (params.subscriptionFrequencyId && params.subscriptionDiscountId) {
            await addProductAndSubscriptionToReceipt(
                params.externalProductId.toString(),
                params.subscriptionMinimumSalesQuantity ?? 1,
                params.subscriptionDiscountId.toString(),
                params.subscriptionFrequencyId.toString()
            );
        } else {
            await addToReceipt(params.externalProductId.toString(), params.minimumSalesQuantity ?? 1);
        }
    } catch (error) {
        throw new Error('Could not add product');
    }
}

export function useAddProduct() {
    const GA4Tracking = useGA4Tracking();
    const { isPosRequest } = usePointOfSaleRequest();

    function addProductTracking(
        trackingInfo: ProductActionProps['BuyNow']['trackingInfo'],
        additionalGA4Params?: EventParamDecorated[]
    ) {
        const GA4TrackingObject = addToCartTypeEventCategoryMapper(
            trackingInfo.eventCategory,
            trackingInfo.eventLabel?.toString()
        );
        const eventParams = additionalGA4Params
            ? [...additionalGA4Params, ...(GA4TrackingObject.eventParams || [])]
            : GA4TrackingObject.eventParams;

        GA4Tracking({ ...GA4TrackingObject, eventParams });

        createActionTracking(
            trackingInfo,
            { eventCategory: 'none', eventAction: 'addToBasket', eventLabel: 'normal' },
            getEventLabelByContext
        );

        if (trackingInfo?.serverSideTrackingModel) {
            serverSideTracking(
                trackingInfo.serverSideTrackingModel.data,
                trackingInfo.serverSideTrackingModel.eventType
            );
        }
    }

    return async function AddProduct(
        { params, trackingInfo }: ProductActionProps['BuyNow'],
        additionalGA4Params?: EventParamDecorated[]
    ) {
        let response;
        try {
            if (isPosRequest) {
                response = await addToBasketPos(params);
            } else {
                const addToBasket = await loadAddToBasketFunc();
                response = await addToBasket(params);
            }
        } catch (error) {
            throw new Error('Could not add product');
        }

        try {
            addProductTracking(trackingInfo, additionalGA4Params);
        } catch (err) {
            console.error(`Could not track AddProduct: ${err}`);
        }
        return response;
    };
}
